import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import Footer from './Footer';
import { AgentType } from '../../types';
import { AGENTS } from '../../constants/agents';
import { useAgents } from '../../hooks/useAgents';

const DashboardLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<AgentType | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { loading: agentsLoading } = useAgents();
  
  const currentAgent = useMemo(() => {
    return AGENTS.find(agent => agent.id === selectedAgentId) || AGENTS[0];
  }, [selectedAgentId]);

  // Map agent IDs to routes (for navigation)
  const agentRoutes = useMemo<Record<AgentType, string>>(() => ({
    [AgentType.WELCOME]: '/app/dashboard',
    [AgentType.DEVOCIONAL_PESSOAL]: '/app/agents/personal-devotional',
    [AgentType.WORD_ANALYZER]: '/app/agents/word-analyzer',
    [AgentType.TRANSLATION_COMPARATOR]: '/app/agents/translation-comparator',
    [AgentType.BIBLE_STUDY_CREATOR]: '/app/agents/bible-study-creator',
    [AgentType.LITERARY_FORM]: '/app/agents/literary-form-identifier',
    [AgentType.BIBLICAL_UNIT_IDENTIFIER]: '/app/agents/unit-identifier',
    [AgentType.CHARACTERS]: '/app/agents/character-analyzer',
    [AgentType.CONTEXT_RESEARCHER]: '/app/agents/context-researcher',
    [AgentType.SERMON_BUILDER]: '/app/agents/sermon-builder',
    [AgentType.ILLUSTRATION_GENERATOR]: '/app/agents/illustration-generator',
    [AgentType.TARGET_AUDIENCE_DEFINER]: '/app/agents/target-audience-definer',
    [AgentType.LESSON_PREPARER]: '/app/agents/lesson-preparer',
    [AgentType.POST_CREATOR]: '/app/agents/post-creator',
    [AgentType.TEXT_UPDATER]: '/app/agents/text-updater',
    [AgentType.WORLDVIEW_ANALYZER]: '/app/agents/worldview-analyzer',
    [AgentType.INTERPRETATION_HISTORIAN]: '/app/agents/interpretation-historian',
    [AgentType.DESCRIBE_THEOLOGIES]: '/app/agents/theology-describer',
    [AgentType.EXPLORE_THEMES]: '/app/agents/theme-explorer',
    [AgentType.BIOGRAPH_CHARACTERS]: '/app/agents/character-biographer',
    [AgentType.RELATE_TEXTS]: '/app/agents/text-relator',
    [AgentType.GENERATE_BOOK_THEOLOGY]: '/app/agents/theology-presenter',
  }), []);

  // Create reverse mapping (route -> AgentType)
  const routeToAgent = useMemo<Record<string, AgentType>>(() => {
    return Object.fromEntries(
      Object.entries(agentRoutes).map(([agentId, route]) => [route, agentId as AgentType])
    );
  }, [agentRoutes]);

  // Sync selectedAgentId with current route
  useEffect(() => {
    const pathname = location.pathname;
    
    // First check for exact dashboard routes - these should always select WELCOME
    if (
      pathname === '/app' ||
      pathname === '/app/dashboard' ||
      pathname === '/' ||
      pathname === '/dashboard'
    ) {
      setSelectedAgentId(AgentType.WELCOME);
      return;
    }
    
    // Check if current path matches any agent route
    const matchedAgentId = routeToAgent[pathname];
    
    if (matchedAgentId) {
      setSelectedAgentId(matchedAgentId);
    } else {
      // Default to WELCOME if no match found
      setSelectedAgentId(AgentType.WELCOME);
    }
  }, [location.pathname, routeToAgent]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) { 
        setIsSidebarOpen(false);
      }
      // Desktop mantém o estado atual (não força abrir)
    };
    window.addEventListener('resize', handleResize);
    handleResize(); 
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSelectAgent = (agentId: AgentType) => {
    setSelectedAgentId(agentId);
    
    // Fechar sidebar apenas em mobile após seleção
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
    
    const route = agentRoutes[agentId];
    if (route) {
      navigate(route);
    }
  };

  return (
    <div className="flex h-screen font-sans">
      <Sidebar 
        selectedAgentId={selectedAgentId}
        onSelectAgent={handleSelectAgent}
        isOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
      />
      <div
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ease-in-out
                    ${isSidebarOpen ? 'md:ml-64' : 'md:ml-[5rem]'}`}
      >
        <Navbar 
          toggleSidebar={toggleSidebar} 
          isSidebarOpen={isSidebarOpen}
          agentName={currentAgent?.name}
          agentCategory={currentAgent?.category}
        />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 bg-background">
          <Outlet context={{ onSelectAgent: handleSelectAgent }} />
        </main>
        <Footer />
      </div>
      
      {/* Toast de Loading Centralizado */}
      {agentsLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-lg p-6 shadow-xl flex flex-col items-center gap-4 min-w-[200px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary"></div>
            <p className="text-sm font-medium text-foreground">Carregando agentes...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;
